import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Key, Plus, Trash2, Copy, AlertTriangle } from "lucide-react";
import { useApiKeys } from "hooks/useApiKeys";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useToast } from "components/Toast";
import ConfirmDialog from "components/ConfirmDialog";
import Skeleton from "components/Skeleton";

const ALL_PERMISSIONS = [
  { key: "applications:write", label: "Applications Write" },
  { key: "applications:read", label: "Applications Read" },
  { key: "schema:read", label: "Schema Read" },
  { key: "webhooks:manage", label: "Webhooks Manage" },
];

export default function ApiKeysTab() {
  const { user } = useAuth();
  const isAdmin = isInternal(user);
  const { keys, loading, refetch } = useApiKeys();
  const { toast } = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: () => {} });

  // Form state
  const [partnerIdInput, setPartnerIdInput] = useState("");
  const [partnerNameInput, setPartnerNameInput] = useState("");
  const [permissions, setPermissions] = useState(
    ALL_PERMISSIONS.map((p) => p.key)
  );

  const togglePermission = (perm) => {
    setPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleCreate = async () => {
    const partnerId = isAdmin ? partnerIdInput.trim() : user.companyId;
    const partnerName = partnerNameInput.trim();

    if (!partnerName || (isAdmin && !partnerId)) {
      toast({ status: "destructive", title: "Please fill in all fields" });
      return;
    }
    if (permissions.length === 0) {
      toast({
        status: "destructive",
        title: "Select at least one permission",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await makeApiRequest(
        "post",
        apiRoutes.createApiKey,
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        { partnerId, partnerName, permissions }
      );
      setCreatedKey(res.data?.apiKey || res.data?.data?.apiKey);
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to create API key",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = (keyId) => {
    setConfirmDialog({
      open: true,
      title: "Revoke API key",
      description: "Revoke this API key? This cannot be undone.",
      confirmLabel: "Revoke",
      variant: "destructive",
      onConfirm: async () => {
        setConfirmDialog((c) => ({ ...c, open: false }));
        try {
          await makeApiRequest(
            "delete",
            apiRoutes.revokeApiKey(keyId),
            { id: user.id, accessToken: user.accessToken },
            user.companyId
          );
          toast({ status: "success", title: "API key revoked" });
          refetch();
        } catch (err) {
          toast({
            status: "destructive",
            title: "Failed to revoke key",
            description: err.response?.data?.error || "Unknown error",
          });
        }
      },
    });
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    toast({ status: "success", title: "Copied to clipboard" });
  };

  const closeModal = () => {
    setShowCreate(false);
    setCreatedKey(null);
    setPartnerIdInput("");
    setPartnerNameInput("");
    setPermissions(ALL_PERMISSIONS.map((p) => p.key));
    refetch();
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton.Rect className="h-10 w-full" />
        <Skeleton.Rect className="h-10 w-full" />
        <Skeleton.Rect className="h-10 w-2/3" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          {keys.length} API key{keys.length !== 1 ? "s" : ""}
        </h3>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreate(true)}
        >
          <Plus className="h-4 w-4" />
          Create Key
        </button>
      </div>

      {/* Table */}
      {keys.length > 0 ? (
        <div className="surface rounded-xl ring-1 ring-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Key Prefix</th>
                {isAdmin && (
                  <th className="px-4 py-3 font-medium">Partner</th>
                )}
                <th className="px-4 py-3 font-medium">Permissions</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Last Used</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr
                  key={k._id}
                  className="border-b border-border last:border-0"
                >
                  <td className="px-4 py-3 font-mono text-xs">
                    {k.keyPrefix}...
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <span className="text-xs">{k.partnerId}</span>
                      <br />
                      <span className="text-xs text-muted-foreground">
                        {k.partnerName}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(k.permissions || []).map((p) => (
                        <span
                          key={p}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground"
                        >
                          {p.split(":")[1]}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        k.status === "active"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}
                    >
                      {k.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {k.lastUsedAt
                      ? new Date(k.lastUsedAt).toLocaleDateString()
                      : "Never"}
                  </td>
                  <td className="px-4 py-3">
                    {k.status === "active" && (
                      <button
                        className="btn btn-ghost btn-xs text-destructive"
                        onClick={() => handleRevoke(k._id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="surface rounded-xl ring-1 ring-border p-8 text-center">
          <Key className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            No API keys yet. Create one to get started.
          </p>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        {...confirmDialog}
        onCancel={() => setConfirmDialog((c) => ({ ...c, open: false }))}
      />

      {/* Create Modal — rendered via portal (SOUND-239) so it escapes the
          SettingsLayout stacking context that otherwise traps fixed
          descendants of <main>. */}
      {showCreate && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-md shadow-lg">
            {createdKey ? (
              /* Key Created View */
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">API Key Created</h3>
                <div className="flex items-center gap-2 bg-muted rounded-lg p-3">
                  <code className="flex-1 text-xs break-all font-mono">
                    {createdKey}
                  </code>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => copyToClipboard(createdKey)}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex items-start gap-2 text-xs text-yellow-600 dark:text-yellow-400">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    This key will not be shown again. Store it securely.
                  </span>
                </div>
                <button className="btn btn-primary w-full" onClick={closeModal}>
                  Done
                </button>
              </div>
            ) : (
              /* Create Form */
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Create API Key</h3>

                {isAdmin && (
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">
                      Partner ID
                    </label>
                    <input
                      type="text"
                      className="input input-bordered w-full"
                      placeholder="e.g. acme-corp"
                      value={partnerIdInput}
                      onChange={(e) => setPartnerIdInput(e.target.value)}
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">
                    Partner Name
                  </label>
                  <input
                    type="text"
                    className="input input-bordered w-full"
                    placeholder="e.g. ACME Corporation"
                    value={partnerNameInput}
                    onChange={(e) => setPartnerNameInput(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    {ALL_PERMISSIONS.map((p) => (
                      <label
                        key={p.key}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={permissions.includes(p.key)}
                          onChange={() => togglePermission(p.key)}
                          className="rounded border-border"
                        />
                        <span>{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    className="btn btn-ghost flex-1"
                    onClick={closeModal}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary flex-1"
                    onClick={handleCreate}
                    disabled={submitting}
                  >
                    {submitting ? "Creating..." : "Create Key"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
